// Costos por Plan de Manejo — por hectárea.
// Resume el costo teórico de insumos del plan (dosis × costo_promedio ERP),
// agregado por etapa → ciclo → producto, para un ciclo de cosecha.
// Incluye desglose por departamento y un simulador área/densidad → costo lote/planta.
//
// Fuente de datos: RPC app_costos_plan (vista v_app_plan_costos).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChevronDown, ChevronRight, Loader2, RefreshCw, Printer, AlertTriangle, Coins,
} from 'lucide-react';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import {
  btnPrimary, btnSecondary, inputCls, labelCls, pageTitleCls, pageSubtitleCls,
} from '../../components/ui';
import type { CicloCosecha, CostosPlan, CostoEtapa, CostoCiclo } from '../../types/planManejo';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtCRC(n: number | null | undefined, dec = 0): string {
  const v = Number(n);
  if (!Number.isFinite(v) || v === 0) return '—';
  return '₡' + v.toLocaleString('es-CR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}
function fmtUSD(n: number | null | undefined, dec = 2): string {
  const v = Number(n);
  if (!Number.isFinite(v) || v === 0) return '—';
  return '$' + v.toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}
function fmtNum(n: number | null | undefined, dec = 2): string {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return v.toLocaleString('es-CR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

// Segmento de la dona (participación por etapa)
interface DonaSeg { etapa: string; costo_ha: number; pct: number; desde: number; color: string; }

// Paleta de la dona — tonos del sistema (sin chocar con el ámbar de "sin costo")
const DONA_COLORS = [
  'var(--tone-success-text)', 'var(--tone-info-text)', 'var(--tone-purple-text)',
  'var(--emp-accent)', 'var(--tone-warn-text)', '#8b5cf6', '#0ea5e9', '#14b8a6',
  '#f97316', '#64748b',
];

// 'TODAS' = sin filtro (plan completo). El resto filtra por ciclo_cosecha.
type FiltroCosecha = CicloCosecha | 'TODAS';

const COSECHAS: { value: FiltroCosecha; label: string }[] = [
  { value: 'TODAS', label: 'Todas las etapas (plan completo)' },
  { value: 'PC', label: 'Primera Cosecha (PC)' },
  { value: 'RC', label: 'Segunda Cosecha (RC)' },
  { value: 'RC2', label: 'Tercera Cosecha (RC2)' },
  { value: 'SEMILLERO', label: 'Semillero' },
  { value: 'BARBECHO', label: 'Barbecho' },
];

const DEPTO_COLORS = [
  'var(--tone-success-text)', 'var(--tone-info-text)', 'var(--tone-warn-text)',
  'var(--tone-purple-text)', 'var(--emp-accent-txt)', 'var(--ink-muted)',
];

// Fila plana de la vista v_app_plan_costos (fallback cuando la RPC no está).
interface FilaVista {
  empresa_id: number;
  etapa_id: number; etapa: string; ciclo_cosecha: CicloCosecha; etapa_orden: number;
  ciclo_id: number; ciclo: string; dias: number;
  producto_id: number; articulo: string; nombre_producto: string; departamento: string | null;
  dosis: number; medida: string | null; producto_orden: number;
  costo_unitario: number; costo_ha: number; sin_costo: boolean; en_bodega_finca: boolean;
  // Multimoneda (vista ampliada 20260624_plan_costos_multimoneda.sql)
  moneda_costo?: 'CRC' | 'USD'; tipo_cambio_costo?: number;
  costo_unitario_orig?: number; costo_unitario_usd?: number; costo_ha_usd?: number;
}

// Arma el árbol etapa→ciclo→producto desde filas planas de la vista, replicando
// el formato de la RPC app_costos_plan. Se usa como fallback en el cliente.
function armarArbol(filas: FilaVista[], empresaId: number, cosecha: string | null): CostosPlan {
  const num = (v: any) => Number(v) || 0;
  const etapasMap = new Map<number, CostoEtapa & { _orden: number; _ciclos: Map<number, CostoCiclo & { _dias: number }> }>();

  for (const f of filas) {
    let et = etapasMap.get(f.etapa_id);
    if (!et) {
      et = {
        etapa_id: f.etapa_id, etapa: f.etapa, ciclo_cosecha: f.ciclo_cosecha,
        costo_ha: 0, n_sin_costo: 0, ciclos: [],
        _orden: num(f.etapa_orden), _ciclos: new Map(),
      };
      etapasMap.set(f.etapa_id, et);
    }
    let ci = et._ciclos.get(f.ciclo_id);
    if (!ci) {
      ci = {
        ciclo_id: f.ciclo_id, ciclo: f.ciclo, dias: num(f.dias),
        costo_ha: 0, n_productos: 0, n_sin_costo: 0, productos: [], _dias: num(f.dias),
      };
      et._ciclos.set(f.ciclo_id, ci);
    }
    const sinCosto = !!f.sin_costo;
    ci.productos.push({
      producto_id: f.producto_id, articulo: f.articulo, nombre_producto: f.nombre_producto,
      departamento: f.departamento, dosis: num(f.dosis), medida: f.medida,
      costo_unitario: num(f.costo_unitario), costo_ha: num(f.costo_ha),
      moneda_costo: (f.moneda_costo === 'USD' ? 'USD' : 'CRC'),
      tipo_cambio_costo: num(f.tipo_cambio_costo) || 1,
      costo_unitario_orig: num(f.costo_unitario_orig),
      costo_unitario_usd: num(f.costo_unitario_usd),
      costo_ha_usd: num(f.costo_ha_usd),
      sin_costo: sinCosto, en_bodega_finca: !!f.en_bodega_finca,
    });
    ci.costo_ha += num(f.costo_ha);
    ci.costo_ha_usd = (ci.costo_ha_usd ?? 0) + num(f.costo_ha_usd);
    ci.n_productos += 1;
    if (sinCosto) ci.n_sin_costo += 1;
    et.costo_ha += num(f.costo_ha);
    et.costo_ha_usd = (et.costo_ha_usd ?? 0) + num(f.costo_ha_usd);
    if (sinCosto) et.n_sin_costo += 1;
  }

  const etapas: CostoEtapa[] = [...etapasMap.values()]
    .sort((a, b) => a._orden - b._orden || a.etapa.localeCompare(b.etapa))
    .map(et => ({
      etapa_id: et.etapa_id, etapa: et.etapa, ciclo_cosecha: et.ciclo_cosecha,
      costo_ha: et.costo_ha, costo_ha_usd: et.costo_ha_usd ?? 0, n_sin_costo: et.n_sin_costo,
      ciclos: [...et._ciclos.values()]
        .sort((a, b) => a._dias - b._dias || a.ciclo.localeCompare(b.ciclo))
        .map(({ _dias, ...c }) => c),
    }));

  // Desglose por departamento
  const deptoMap = new Map<string, number>();
  for (const f of filas) {
    const d = (f.departamento && f.departamento.trim()) || 'Sin departamento';
    deptoMap.set(d, (deptoMap.get(d) ?? 0) + num(f.costo_ha));
  }
  const por_departamento = [...deptoMap.entries()]
    .map(([departamento, costo_ha]) => ({ departamento, costo_ha }))
    .sort((a, b) => b.costo_ha - a.costo_ha);

  return {
    empresa_id: empresaId,
    ciclo_cosecha: (cosecha as CicloCosecha) ?? null,
    costo_ha_total: etapas.reduce((s, e) => s + e.costo_ha, 0),
    costo_ha_total_usd: etapas.reduce((s, e) => s + (e.costo_ha_usd ?? 0), 0),
    n_sin_costo: etapas.reduce((s, e) => s + e.n_sin_costo, 0),
    etapas,
    por_departamento,
  };
}

// ─── Página ─────────────────────────────────────────────────────────────────

export function PlanCostosPage() {
  const empresaId = useEmpresaId();
  const [cosecha, setCosecha] = useState<FiltroCosecha>('TODAS');
  const [data, setData] = useState<CostosPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [abiertas, setAbiertas] = useState<Set<number>>(new Set());

  // Simulador
  const [areaHa, setAreaHa] = useState<string>('1');
  const [densidad, setDensidad] = useState<string>('62000');

  // Nombre de empresa para el encabezado de los reportes impresos.
  const [empresaNombre, setEmpresaNombre] = useState('');
  useEffect(() => {
    supabase.from('empresas').select('nombre').eq('id', empresaId).maybeSingle()
      .then(({ data }) => setEmpresaNombre(data?.nombre || ''));
  }, [empresaId]);

  // Modo de impresión: 'detalle' = tabla etapa/ciclo completa; 'resumen' = por
  // etapa + dona. Se setea justo antes de window.print() y el CSS muestra el
  // contenedor correspondiente.
  const [modoPrint, setModoPrint] = useState<'detalle' | 'resumen'>('detalle');
  const imprimir = useCallback((modo: 'detalle' | 'resumen') => {
    setModoPrint(modo);
    // Esperar al repaint para que el contenedor correcto esté visible al imprimir.
    setTimeout(() => window.print(), 60);
  }, []);

  const cargar = useCallback(async () => {
    setLoading(true); setError('');
    const filtroCosecha = cosecha === 'TODAS' ? null : cosecha;

    // Leemos SIEMPRE la vista v_app_plan_costos y armamos el árbol en el cliente.
    // La vista trae los campos multimoneda (moneda_costo, costo_*_usd) que la RPC
    // app_costos_plan no expone; armar en cliente garantiza CRC + USD consistentes.
    let q = supabase
      .from('v_app_plan_costos')
      .select('*')
      .eq('empresa_id', empresaId)
      .order('etapa_orden').order('dias').order('producto_orden')
      .limit(5000);
    if (filtroCosecha) q = q.eq('ciclo_cosecha', filtroCosecha);
    const { data: filas, error: vErr } = await q;

    if (vErr) {
      setError(vErr.message);
      setData(null); setLoading(false); return;
    }
    const arbol = armarArbol((filas ?? []) as FilaVista[], empresaId, filtroCosecha);
    setData(arbol);
    setAbiertas(new Set(arbol.etapas.map(e => e.etapa_id)));
    setLoading(false);
  }, [empresaId, cosecha]);

  useEffect(() => { cargar(); }, [cargar]);

  function toggleEtapa(id: number) {
    setAbiertas(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }

  const totalHa = data?.costo_ha_total ?? 0;
  const area = Number(areaHa) || 0;
  const dens = Number(densidad) || 0;
  const costoLote = totalHa * area;
  const costoPlanta = dens > 0 ? totalHa / dens : 0;

  const maxDepto = useMemo(
    () => Math.max(1, ...(data?.por_departamento ?? []).map(d => d.costo_ha)),
    [data]
  );

  const totalHaUsd = data?.costo_ha_total_usd ?? 0;
  // ¿Hay productos cuyo costo se ingresó en USD? (para mostrar el bloque multimoneda)
  const hayUsd = useMemo(
    () => (data?.etapas ?? []).some(e => e.ciclos.some(c => c.productos.some(p => p.moneda_costo === 'USD' && !p.sin_costo))),
    [data]
  );

  // Segmentos de la dona: participación por ETAPA sobre el costo total del plan.
  const dona = useMemo(() => {
    const etapas = (data?.etapas ?? []).filter(e => e.costo_ha > 0)
      .sort((a, b) => b.costo_ha - a.costo_ha);
    const total = etapas.reduce((s, e) => s + e.costo_ha, 0) || 1;
    let acc = 0;
    return etapas.map((e, i) => {
      const pct = (e.costo_ha / total) * 100;
      const seg = { etapa: e.etapa, costo_ha: e.costo_ha, pct, desde: acc, color: DONA_COLORS[i % DONA_COLORS.length] };
      acc += pct;
      return seg;
    });
  }, [data]);

  // Gradiente cónico para la dona (CSS puro, sin librería).
  const donaGradient = useMemo(() => {
    if (dona.length === 0) return 'var(--surface-deep)';
    const stops = dona.map(s => `${s.color} ${s.desde.toFixed(2)}% ${(s.desde + s.pct).toFixed(2)}%`);
    return `conic-gradient(${stops.join(', ')})`;
  }, [dona]);

  const cosechaLabel = COSECHAS.find(c => c.value === cosecha)?.label ?? cosecha;
  // Texto corto para totales/KPIs (no usa el label largo del selector).
  const cosechaCorta = cosecha === 'TODAS' ? 'plan completo' : cosecha;

  // Modo "plan completo" usa un tono MORADO propio para resaltar que es el total
  // de TODO el plan (no una sola cosecha). Se elige morado para no chocar con el
  // ámbar de las alertas "sin costo" de esta misma pantalla. El resto usa el verde.
  const planCompleto = cosecha === 'TODAS';
  const tonoAccent = planCompleto ? 'var(--tone-purple-text)' : 'var(--emp-accent-txt)';
  const tonoBorde  = planCompleto ? 'var(--tone-purple-border)' : 'var(--emp-accent)';

  return (
    <div className={`flex h-full flex-col overflow-hidden ${modoPrint === 'resumen' ? 'print-resumen' : 'print-detalle'}`}>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .no-print { display: none !important; }
          @page { margin: 14mm; }
          /* Mostrar SOLO el contenedor del modo elegido (clase en el root). */
          .print-detalle #costos-print, .print-detalle #costos-print * { visibility: visible; }
          .print-resumen #costos-print-resumen, .print-resumen #costos-print-resumen * { visibility: visible; }
          /* display:none inline no se revierte con visibility → forzar display. */
          .print-detalle #costos-print { display: block !important; position: absolute; inset: 0; width: 100%; }
          .print-resumen #costos-print-resumen { display: block !important; position: absolute; inset: 0; width: 100%; }
          /* Forzar que los colores (banda, thead, dona) salgan en papel. */
          #costos-print, #costos-print *, #costos-print-resumen, #costos-print-resumen * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
        /* ── Estilo calidad ERP para ambos reportes impresos ── */
        #costos-print, #costos-print-resumen {
          font-family: 'Segoe UI', Arial, sans-serif; color: #1e293b; background: #fff;
          padding: 4px; font-size: 12px;
        }
        .rep-head { display: table; width: 100%; border-bottom: 2px solid #16a34a; padding-bottom: 10px; margin-bottom: 16px; }
        .rep-head .left { display: table-cell; vertical-align: bottom; }
        .rep-head .right { display: table-cell; text-align: right; vertical-align: bottom; color: #64748b; font-size: 10px; }
        .rep-empresa { font-size: 17px; font-weight: 700; color: #0f172a; line-height: 1.1; }
        .rep-sub { font-size: 10px; color: #64748b; margin-top: 1px; }
        .rep-title { font-size: 13px; font-weight: 600; color: #16a34a; margin-top: 3px; }
        .rep-meta { margin: 0 0 16px; display: table; width: 100%; }
        .rep-meta .box { display: table-cell; padding: 6px 12px; background: #f1f5f9; border: 1px solid #e2e8f0; }
        .rep-meta .box + .box { border-left: none; }
        .rep-meta .lbl { font-size: 8px; text-transform: uppercase; letter-spacing: 0.08em; color: #64748b; display: block; }
        .rep-meta .val { font-size: 15px; font-weight: 700; color: #0f172a; }
        #costos-print table, #costos-print-resumen table { width: 100%; border-collapse: collapse; }
        #costos-print thead th, #costos-print-resumen thead th { background: #16a34a; color: #fff; text-align: left; padding: 6px 8px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; }
        #costos-print th, #costos-print td, #costos-print-resumen th, #costos-print-resumen td { padding: 5px 8px; font-size: 11px; }
        #costos-print tbody td, #costos-print-resumen tbody td { border-bottom: 1px solid #e2e8f0; }
        #costos-print .pr-etapa td { background: #f1f5f9; font-weight: bold; border-top: 1px solid #cbd5e1; }
        #costos-print .pr-ciclo td, #costos-print-resumen .pr-ciclo td { color: #334155; }
        #costos-print-resumen tbody tr:nth-child(even) td { background: #f8fafc; }
        #costos-print .pr-total td, #costos-print-resumen .pr-total td { border-top: 2px solid #0f172a; border-bottom: none; font-weight: bold; font-size: 13px; background: #ecfdf5; }
        .rep-foot { margin-top: 18px; padding-top: 8px; border-top: 1px solid #e2e8f0; font-size: 9px; color: #94a3b8; text-align: center; }
      `}</style>

      {/* Header / controles */}
      <div className="no-print shrink-0 px-5 py-4" style={{ background: 'var(--surface-raised)', borderBottom: '1px solid var(--line)' }}>
        <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <h1 className={pageTitleCls}>Costos del Plan de Manejo</h1>
            <p className={pageSubtitleCls}>Costo de insumos por hectárea · {cosechaLabel} · MD2 Golden</p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className={labelCls}>Cosecha</label>
              <select className={inputCls} value={cosecha} onChange={e => setCosecha(e.target.value as FiltroCosecha)}>
                {COSECHAS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <button type="button" className={`${btnSecondary} flex items-center gap-2`} onClick={cargar} disabled={loading}>
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Recargar
            </button>
            <button type="button" className={`${btnSecondary} flex items-center gap-2`} onClick={() => imprimir('resumen')} disabled={!data || (data.etapas?.length ?? 0) === 0}>
              <Printer size={14} /> Resumen
            </button>
            <button type="button" className={`${btnPrimary} flex items-center gap-2`} onClick={() => imprimir('detalle')} disabled={!data || (data.etapas?.length ?? 0) === 0}>
              <Printer size={14} /> Detalle
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 sm:p-5">
        {error && (
          <div className="no-print mb-4 flex items-start gap-3 rounded-lg border px-4 py-3 text-sm"
            style={{ background: 'var(--tone-warn-bg)', borderColor: 'var(--tone-warn-border)', color: 'var(--tone-warn-ink)' }}>
            <AlertTriangle size={17} style={{ color: 'var(--tone-warn-text)', flexShrink: 0, marginTop: 1 }} />
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={26} className="animate-spin" style={{ color: 'var(--emp-accent)' }} />
          </div>
        ) : !data || (data.etapas?.length ?? 0) === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-20">
            <Coins size={34} style={{ color: 'var(--ink-faint)' }} />
            <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>Sin productos costeables para {cosechaLabel}.</p>
            <p className="text-xs text-center max-w-md" style={{ color: 'var(--ink-faint)' }}>
              {cosecha === 'TODAS'
                ? `No hay etapas activas con productos en la empresa ${empresaId}. Verificá que el Plan de Manejo tenga etapas/ciclos/productos activos.`
                : <>No hay etapas con cosecha <strong>{cosecha}</strong>. Probá con <strong>«Todas las etapas»</strong> en el selector de arriba.</>}
            </p>
          </div>
        ) : (
          <div className="grid gap-5 lg:grid-cols-[1fr_22rem]">

            {/* ── Columna izquierda: árbol + total ── */}
            <div className="space-y-4">
              {/* KPIs */}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <KpiCard label={`Costo / ha · ${cosechaCorta}`} value={fmtCRC(totalHa)} sub={hayUsd ? `≈ ${fmtUSD(totalHaUsd)}` : undefined} accent tone={planCompleto ? 'purple' : undefined} />
                <KpiCard label="Costo / ha (USD)" value={fmtUSD(totalHaUsd)} />
                <KpiCard label="Etapas" value={String(data.etapas.length)} />
                <KpiCard label="Sin costo" value={String(data.n_sin_costo)} warn={data.n_sin_costo > 0} />
              </div>

              {data.n_sin_costo > 0 && (
                <div className="flex items-start gap-2 rounded-lg border px-3 py-2 text-xs"
                  style={{ background: 'var(--tone-warn-bg)', borderColor: 'var(--tone-warn-border)', color: 'var(--tone-warn-ink)' }}>
                  <AlertTriangle size={14} style={{ color: 'var(--tone-warn-text)', flexShrink: 0, marginTop: 1 }} />
                  <span>
                    {data.n_sin_costo} producto{data.n_sin_costo > 1 ? 's' : ''} sin costo en inventario (falta ingreso).
                    No suma{data.n_sin_costo > 1 ? 'n' : ''} al total — el costo real será mayor cuando se carguen las compras.
                  </span>
                </div>
              )}

              {/* Árbol etapa → ciclo */}
              <div className="rounded-lg overflow-hidden"
                style={{
                  border: planCompleto ? `1.5px solid ${tonoBorde}` : '1px solid var(--line)',
                  boxShadow: planCompleto ? '0 0 0 3px var(--tone-purple-bg)' : undefined,
                }}>
                {data.etapas.map((et, i) => (
                  <EtapaBloque
                    key={et.etapa_id}
                    etapa={et}
                    abierta={abiertas.has(et.etapa_id)}
                    onToggle={() => toggleEtapa(et.etapa_id)}
                    first={i === 0}
                  />
                ))}
                {/* Total general */}
                <div className="flex items-center justify-between px-4 py-3"
                  style={{
                    borderTop: `2px solid ${tonoBorde}`,
                    background: planCompleto ? 'var(--tone-purple-bg)' : 'var(--surface-raised)',
                  }}>
                  <span className="text-sm font-bold uppercase tracking-wide"
                    style={{ color: planCompleto ? tonoAccent : 'var(--ink)' }}>
                    {planCompleto ? 'Total plan completo / ha' : `Total ciclo ${cosecha} / ha`}
                  </span>
                  <span className="text-right">
                    <span className="block text-base font-mono font-bold tabular-nums" style={{ color: tonoAccent }}>
                      {fmtCRC(totalHa)}
                    </span>
                    {hayUsd && (
                      <span className="block text-[11px] font-mono tabular-nums" style={{ color: 'var(--ink-faint)' }}>
                        ≈ {fmtUSD(totalHaUsd)}
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* ── Columna derecha: dona + simulador + desglose depto ── */}
            <div className="space-y-4">
              {/* Dona: participación por etapa */}
              <div className="rounded-lg p-4" style={{ background: 'var(--surface-raised)', border: '1px solid var(--line)' }}>
                <p className="text-[11px] font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--ink-faint)' }}>
                  Participación por etapa
                </p>
                {dona.length === 0 ? (
                  <p className="text-xs" style={{ color: 'var(--ink-faint)' }}>Sin datos costeables.</p>
                ) : (
                  <div className="flex items-center gap-4">
                    {/* Anillo */}
                    <div className="relative shrink-0" style={{ width: 116, height: 116 }}>
                      <div className="absolute inset-0 rounded-full" style={{ background: donaGradient }} />
                      <div className="absolute rounded-full flex flex-col items-center justify-center"
                        style={{ inset: 22, background: 'var(--surface-raised)' }}>
                        <span className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--ink-faint)' }}>Total/ha</span>
                        <span className="text-[11px] font-mono font-bold tabular-nums" style={{ color: tonoAccent }}>{fmtCRC(totalHa)}</span>
                      </div>
                    </div>
                    {/* Leyenda: color · etapa · monto/ha · % */}
                    <div className="flex-1 min-w-0 space-y-1.5">
                      {dona.map(s => (
                        <div key={s.etapa} className="flex items-center gap-2">
                          <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: s.color }} />
                          <span className="text-[11px] truncate flex-1" style={{ color: 'var(--ink-muted)' }} title={s.etapa}>{s.etapa}</span>
                          <span className="text-[10px] font-mono tabular-nums shrink-0" style={{ color: 'var(--ink-faint)' }}>{fmtCRC(s.costo_ha)}</span>
                          <span className="text-[11px] font-mono tabular-nums shrink-0 w-10 text-right" style={{ color: 'var(--ink)' }}>{s.pct.toFixed(1)}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Simulador */}
              <div className="rounded-lg p-4" style={{ background: 'var(--surface-raised)', border: '1px solid var(--line)' }}>
                <p className="text-[11px] font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--ink-faint)' }}>
                  Simulador de lote
                </p>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className={labelCls}>Área (ha)</label>
                    <input className={inputCls} type="number" step="0.01" min="0" value={areaHa}
                      onChange={e => setAreaHa(e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls}>Densidad (pl/ha)</label>
                    <input className={inputCls} type="number" step="100" min="0" value={densidad}
                      onChange={e => setDensidad(e.target.value)} />
                  </div>
                </div>
                <div className="space-y-2">
                  <SimRow label="Costo total del lote" value={fmtCRC(costoLote)} strong />
                  <SimRow label="Costo por planta" value={costoPlanta > 0 ? fmtCRC(costoPlanta, 2) : '—'} />
                  <SimRow label="Plantas estimadas" value={(area * dens).toLocaleString('es-CR', { maximumFractionDigits: 0 })} />
                </div>
              </div>

              {/* Desglose por departamento */}
              <div className="rounded-lg p-4" style={{ background: 'var(--surface-raised)', border: '1px solid var(--line)' }}>
                <p className="text-[11px] font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--ink-faint)' }}>
                  Costo / ha por departamento
                </p>
                <div className="space-y-2.5">
                  {data.por_departamento.map((d, i) => (
                    <div key={d.departamento}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] truncate" style={{ color: 'var(--ink-muted)' }}>{d.departamento}</span>
                        <span className="text-[11px] font-mono tabular-nums" style={{ color: 'var(--ink)' }}>{fmtCRC(d.costo_ha)}</span>
                      </div>
                      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--surface-deep)' }}>
                        <div className="h-full rounded-full" style={{
                          width: `${Math.max(2, (d.costo_ha / maxDepto) * 100)}%`,
                          background: DEPTO_COLORS[i % DEPTO_COLORS.length],
                        }} />
                      </div>
                    </div>
                  ))}
                  {data.por_departamento.length === 0 && (
                    <p className="text-xs" style={{ color: 'var(--ink-faint)' }}>Sin datos.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Vista imprimible: DETALLE (etapa → ciclo) ── */}
        {data && (data.etapas?.length ?? 0) > 0 && (
          <PrintView data={data} cosechaLabel={cosechaLabel} empresaNombre={empresaNombre}
            area={area} dens={dens} costoLote={costoLote} costoPlanta={costoPlanta} />
        )}

        {/* ── Vista imprimible: RESUMEN por plan + dona ── */}
        {data && (data.etapas?.length ?? 0) > 0 && (
          <ResumenPrintView data={data} cosechaLabel={cosechaLabel} empresaNombre={empresaNombre}
            dona={dona} hayUsd={hayUsd} totalHa={totalHa} totalHaUsd={totalHaUsd} />
        )}
      </div>
    </div>
  );
}

// ─── Sub-componentes ──────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, accent, warn, tone }: {
  label: string; value: string; sub?: string; accent?: boolean; warn?: boolean; tone?: 'purple';
}) {
  // 'purple' = modo plan completo: tarjeta resaltada con tono morado.
  const purple = tone === 'purple';
  const valueColor = warn ? 'var(--tone-warn-text)'
    : purple ? 'var(--tone-purple-text)'
    : accent ? 'var(--emp-accent-txt)' : 'var(--ink)';
  return (
    <div className="rounded-lg px-3 py-2.5" style={{
      background: purple ? 'var(--tone-purple-bg)' : 'var(--surface-raised)',
      border: `1px solid ${purple ? 'var(--tone-purple-border)' : warn ? 'var(--tone-warn-border)' : 'var(--line)'}`,
    }}>
      <p className="text-[10px] uppercase tracking-wide mb-0.5" style={{ color: 'var(--ink-faint)' }}>{label}</p>
      <p className="text-lg font-mono font-bold tabular-nums" style={{ color: valueColor }}>
        {value}
      </p>
      {sub && <p className="text-[11px] font-mono tabular-nums" style={{ color: 'var(--ink-faint)' }}>{sub}</p>}
    </div>
  );
}

function SimRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>{label}</span>
      <span className={`font-mono tabular-nums ${strong ? 'text-sm font-bold' : 'text-xs'}`}
        style={{ color: strong ? 'var(--emp-accent-txt)' : 'var(--ink)' }}>{value}</span>
    </div>
  );
}

function EtapaBloque({ etapa, abierta, onToggle, first }: {
  etapa: CostoEtapa; abierta: boolean; onToggle: () => void; first: boolean;
}) {
  return (
    <div style={{ borderTop: first ? 'none' : '1px solid var(--line-dim)' }}>
      <button onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-2.5 transition-colors"
        style={{ background: 'var(--surface-base)' }}>
        <span className="flex items-center gap-2 min-w-0">
          {abierta ? <ChevronDown size={14} style={{ color: 'var(--ink-faint)' }} />
                   : <ChevronRight size={14} style={{ color: 'var(--ink-faint)' }} />}
          <span className="text-sm font-semibold truncate" style={{ color: 'var(--ink)' }}>{etapa.etapa}</span>
          {etapa.n_sin_costo > 0 && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full border"
              style={{ borderColor: 'var(--tone-warn-border)', color: 'var(--tone-warn-text)', background: 'transparent' }}
              title="Total de productos sin costo en los ciclos de esta etapa">
              {etapa.n_sin_costo} sin costo
            </span>
          )}
        </span>
        <span className="text-sm font-mono font-bold tabular-nums shrink-0 ml-3" style={{ color: 'var(--ink)' }}>
          {fmtCRC(etapa.costo_ha)}
        </span>
      </button>
      {abierta && (
        <div style={{ background: 'var(--surface-deep)' }}>
          {etapa.ciclos.map(c => <CicloFila key={c.ciclo_id} ciclo={c} />)}
        </div>
      )}
    </div>
  );
}

function CicloFila({ ciclo }: { ciclo: CostoCiclo }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderTop: '1px solid var(--line-dim)' }}>
      <button onClick={() => setOpen(o => !o)}
        className="w-full grid items-center gap-2 px-4 py-1.5 text-left"
        style={{ gridTemplateColumns: '1rem 1fr 3.5rem 6rem' }}>
        {open ? <ChevronDown size={11} style={{ color: 'var(--ink-faint)' }} />
              : <ChevronRight size={11} style={{ color: 'var(--ink-faint)' }} />}
        <span className="flex items-center gap-1.5 min-w-0">
          <span className="text-xs truncate"
            style={{ color: ciclo.n_sin_costo > 0 ? 'var(--tone-warn-text)' : 'var(--ink-muted)' }}>
            {ciclo.ciclo}
          </span>
          {ciclo.n_sin_costo > 0 && (
            <span className="shrink-0 inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full"
              style={{ background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)' }}
              title={`${ciclo.n_sin_costo} producto(s) sin costo en inventario`}>
              <AlertTriangle size={9} /> {ciclo.n_sin_costo} sin costo
            </span>
          )}
        </span>
        <span className="text-[10px] font-mono text-right" style={{ color: 'var(--ink-faint)' }}>día {ciclo.dias}</span>
        <span className="text-xs font-mono text-right tabular-nums" style={{ color: 'var(--ink)' }}>{fmtCRC(ciclo.costo_ha)}</span>
      </button>
      {open && (
        <div className="pb-1.5">
          {ciclo.productos.map(p => {
            const esUsd = p.moneda_costo === 'USD' && !p.sin_costo;
            return (
            <div key={p.producto_id} className="grid items-center gap-2 pl-10 pr-4 py-0.5"
              style={{ gridTemplateColumns: '1fr 4.5rem 6.5rem 6rem' }}>
              <span className="text-[11px] truncate flex items-center gap-1.5" style={{ color: p.sin_costo ? 'var(--tone-warn-text)' : 'var(--ink-muted)' }}>
                <span className="truncate">{p.nombre_producto}</span>
                {esUsd && (
                  <span className="shrink-0 text-[8px] font-bold px-1 py-px rounded"
                    style={{ background: 'var(--tone-info-bg)', color: 'var(--tone-info-text)' }}
                    title={`Costo ingresado en USD · TC ₡${fmtNum(p.tipo_cambio_costo, 2)}`}>USD</span>
                )}
              </span>
              <span className="text-[10px] font-mono text-right" style={{ color: 'var(--ink-faint)' }}>
                {fmtNum(p.dosis, 3)} {p.medida ?? ''}
              </span>
              <span className="text-[10px] font-mono text-right leading-tight" style={{ color: 'var(--ink-faint)' }}>
                {p.sin_costo ? 'sin costo' : (
                  esUsd
                    ? <>{fmtUSD(p.costo_unitario_orig)}<span className="block" style={{ color: 'var(--ink-faint)', opacity: 0.7 }}>{fmtCRC(p.costo_unitario)}</span></>
                    : fmtCRC(p.costo_unitario)
                )}
              </span>
              <span className="text-[11px] font-mono text-right tabular-nums" style={{ color: p.sin_costo ? 'var(--tone-warn-text)' : 'var(--ink)' }}>
                {p.sin_costo ? '—' : fmtCRC(p.costo_ha)}
              </span>
            </div>
          );})}
        </div>
      )}
    </div>
  );
}

// ─── Vista imprimible (tabla plana) ───────────────────────────────────────────

function PrintView({ data, cosechaLabel, empresaNombre, area, dens, costoLote, costoPlanta }: {
  data: CostosPlan; cosechaLabel: string; empresaNombre: string;
  area: number; dens: number; costoLote: number; costoPlanta: number;
}) {
  const hoyStr = new Date().toLocaleDateString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  return (
    <div id="costos-print" style={{ display: 'none' }}>
      <div className="rep-head">
        <div className="left">
          <div className="rep-empresa">{empresaNombre || 'Control de Piña'}</div>
          <div className="rep-sub">Control de Piña · MD2 Golden</div>
          <div className="rep-title">Costos del Plan de Manejo — Detalle por hectárea</div>
        </div>
        <div className="right">
          {cosechaLabel}<br />Generado: {hoyStr}
        </div>
      </div>
      <div className="rep-meta">
        <div className="box"><span className="lbl">Costo / ha (₡)</span><span className="val">{fmtCRC(data.costo_ha_total)}</span></div>
        <div className="box"><span className="lbl">Costo / ha (US$)</span><span className="val">{fmtUSD(data.costo_ha_total_usd)}</span></div>
        <div className="box"><span className="lbl">Etapas</span><span className="val">{data.etapas.length}</span></div>
        <div className="box"><span className="lbl">Sin costo</span><span className="val">{data.n_sin_costo}</span></div>
      </div>
      <table>
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>Etapa / Ciclo</th>
            <th style={{ textAlign: 'right' }}>Día</th>
            <th style={{ textAlign: 'right' }}>Productos</th>
            <th style={{ textAlign: 'right' }}>Costo / ha (₡)</th>
            <th style={{ textAlign: 'right' }}>Costo / ha (US$)</th>
          </tr>
        </thead>
        <tbody>
          {data.etapas.map(et => (
            <React.Fragment key={et.etapa_id}>
              <tr className="pr-etapa">
                <td>{et.etapa}</td>
                <td></td>
                <td></td>
                <td style={{ textAlign: 'right' }}>{fmtCRC(et.costo_ha)}</td>
                <td style={{ textAlign: 'right' }}>{fmtUSD(et.costo_ha_usd)}</td>
              </tr>
              {et.ciclos.map(c => (
                <tr key={c.ciclo_id} className="pr-ciclo">
                  <td style={{ paddingLeft: 20 }}>{c.ciclo}</td>
                  <td style={{ textAlign: 'right' }}>{c.dias}</td>
                  <td style={{ textAlign: 'right' }}>{c.n_productos}</td>
                  <td style={{ textAlign: 'right' }}>{fmtCRC(c.costo_ha)}</td>
                  <td style={{ textAlign: 'right' }}>{fmtUSD(c.costo_ha_usd)}</td>
                </tr>
              ))}
            </React.Fragment>
          ))}
          <tr className="pr-total">
            <td>Total ciclo / ha</td>
            <td></td>
            <td></td>
            <td style={{ textAlign: 'right' }}>{fmtCRC(data.costo_ha_total)}</td>
            <td style={{ textAlign: 'right' }}>{fmtUSD(data.costo_ha_total_usd)}</td>
          </tr>
        </tbody>
      </table>

      <div style={{ marginTop: 16, fontSize: 12, background: '#f8fafc', border: '1px solid #e2e8f0', padding: '10px 12px' }}>
        <p style={{ fontWeight: 'bold', marginBottom: 4, color: '#0f172a' }}>Simulación de lote</p>
        <p>Área: {fmtNum(area, 2)} ha · Densidad: {dens.toLocaleString('es-CR')} pl/ha</p>
        <p>Costo total del lote: <strong>{fmtCRC(costoLote)}</strong></p>
        <p>Costo por planta: <strong>{costoPlanta > 0 ? fmtCRC(costoPlanta, 2) : '—'}</strong></p>
      </div>

      {data.n_sin_costo > 0 && (
        <p style={{ marginTop: 12, fontSize: 11, fontStyle: 'italic' }}>
          Nota: {data.n_sin_costo} producto(s) sin costo en inventario no se incluyen en el total.
        </p>
      )}
      <div className="rep-foot">MYA ERP · Control de Piña — Costos del Plan de Manejo</div>
    </div>
  );
}

// ─── Vista imprimible: RESUMEN por plan (etapa) + dona SVG ─────────────────────
// La dona se dibuja en SVG (no conic-gradient) para que SIEMPRE salga en papel,
// sin depender de "imprimir fondos" del navegador.

function donutPath(cx: number, cy: number, rOut: number, rIn: number, a0: number, a1: number): string {
  // Ángulos en grados, 0° arriba, sentido horario. Devuelve un arco anular.
  const rad = (deg: number) => ((deg - 90) * Math.PI) / 180;
  const big = a1 - a0 > 180 ? 1 : 0;
  const xo0 = cx + rOut * Math.cos(rad(a0)), yo0 = cy + rOut * Math.sin(rad(a0));
  const xo1 = cx + rOut * Math.cos(rad(a1)), yo1 = cy + rOut * Math.sin(rad(a1));
  const xi1 = cx + rIn * Math.cos(rad(a1)), yi1 = cy + rIn * Math.sin(rad(a1));
  const xi0 = cx + rIn * Math.cos(rad(a0)), yi0 = cy + rIn * Math.sin(rad(a0));
  return `M ${xo0} ${yo0} A ${rOut} ${rOut} 0 ${big} 1 ${xo1} ${yo1} L ${xi1} ${yi1} A ${rIn} ${rIn} 0 ${big} 0 ${xi0} ${yi0} Z`;
}

// Colores sólidos para impresión (sin var() CSS, que no resuelve bien al imprimir).
const DONA_PRINT_COLORS = [
  '#16a34a', '#0284c7', '#7c3aed', '#15803d', '#d97706',
  '#8b5cf6', '#0ea5e9', '#14b8a6', '#f97316', '#64748b',
];

function ResumenPrintView({ data, cosechaLabel, empresaNombre, dona, hayUsd, totalHa, totalHaUsd }: {
  data: CostosPlan; cosechaLabel: string; empresaNombre: string; dona: DonaSeg[]; hayUsd: boolean;
  totalHa: number; totalHaUsd: number;
}) {
  const etapas = [...data.etapas].sort((a, b) => b.costo_ha - a.costo_ha);
  const total = data.costo_ha_total || 1;
  const hoyStr = new Date().toLocaleDateString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  return (
    <div id="costos-print-resumen" style={{ display: 'none' }}>
      <div className="rep-head">
        <div className="left">
          <div className="rep-empresa">{empresaNombre || 'Control de Piña'}</div>
          <div className="rep-sub">Control de Piña · MD2 Golden</div>
          <div className="rep-title">Costos del Plan de Manejo — Resumen por etapa</div>
        </div>
        <div className="right">
          {cosechaLabel}<br />Generado: {hoyStr}
        </div>
      </div>

      {/* KPIs en cajas estilo ERP */}
      <div className="rep-meta">
        <div className="box"><span className="lbl">Costo / ha (₡)</span><span className="val">{fmtCRC(totalHa)}</span></div>
        {hayUsd && <div className="box"><span className="lbl">Costo / ha (US$)</span><span className="val">{fmtUSD(totalHaUsd)}</span></div>}
        <div className="box"><span className="lbl">Etapas</span><span className="val">{data.etapas.length}</span></div>
        <div className="box"><span className="lbl">Sin costo</span><span className="val">{data.n_sin_costo}</span></div>
      </div>

      {/* Dona SVG + leyenda */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 28, marginBottom: 18 }}>
        <svg width="180" height="180" viewBox="0 0 180 180" style={{ flexShrink: 0 }}>
          {dona.map((s, i) => (
            <path key={s.etapa}
              d={donutPath(90, 90, 80, 48, s.desde * 3.6, (s.desde + s.pct) * 3.6)}
              fill={DONA_PRINT_COLORS[i % DONA_PRINT_COLORS.length]} stroke="#fff" strokeWidth="1" />
          ))}
          <text x="90" y="86" textAnchor="middle" style={{ fontSize: 9, fill: '#666' }}>Total / ha</text>
          <text x="90" y="100" textAnchor="middle" style={{ fontSize: 12, fontWeight: 'bold', fill: '#000' }}>{fmtCRC(totalHa)}</text>
        </svg>
        <table style={{ fontSize: 12, borderCollapse: 'collapse' }}>
          <tbody>
            {dona.map((s, i) => (
              <tr key={s.etapa}>
                {/* Cuadrito de color como SVG (los fondos CSS no imprimen). */}
                <td style={{ paddingRight: 8, verticalAlign: 'middle' }}>
                  <svg width="12" height="12" style={{ display: 'block' }}>
                    <rect width="12" height="12" rx="2" fill={DONA_PRINT_COLORS[i % DONA_PRINT_COLORS.length]} />
                  </svg>
                </td>
                <td style={{ paddingRight: 18 }}>{s.etapa}</td>
                <td style={{ paddingRight: 18, textAlign: 'right' }}>{fmtCRC(s.costo_ha)}</td>
                <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{s.pct.toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Tabla resumen por etapa */}
      <table>
        <thead>
          <tr style={{ borderBottom: '1px solid #000' }}>
            <th style={{ textAlign: 'left' }}>Etapa</th>
            <th style={{ textAlign: 'right' }}>Participación</th>
            <th style={{ textAlign: 'right' }}>Costo / ha (₡)</th>
            <th style={{ textAlign: 'right' }}>Costo / ha (US$)</th>
          </tr>
        </thead>
        <tbody>
          {etapas.map(et => (
            <tr key={et.etapa_id} className="pr-ciclo">
              <td>{et.etapa}</td>
              <td style={{ textAlign: 'right' }}>{((et.costo_ha / total) * 100).toFixed(1)}%</td>
              <td style={{ textAlign: 'right' }}>{fmtCRC(et.costo_ha)}</td>
              <td style={{ textAlign: 'right' }}>{fmtUSD(et.costo_ha_usd)}</td>
            </tr>
          ))}
          <tr className="pr-total">
            <td>Total plan / ha</td>
            <td style={{ textAlign: 'right' }}>100%</td>
            <td style={{ textAlign: 'right' }}>{fmtCRC(data.costo_ha_total)}</td>
            <td style={{ textAlign: 'right' }}>{fmtUSD(data.costo_ha_total_usd)}</td>
          </tr>
        </tbody>
      </table>

      {data.n_sin_costo > 0 && (
        <p style={{ marginTop: 12, fontSize: 11, fontStyle: 'italic' }}>
          Nota: {data.n_sin_costo} producto(s) sin costo en inventario no se incluyen en el total.
        </p>
      )}
      <div className="rep-foot">MYA ERP · Control de Piña — Costos del Plan de Manejo</div>
    </div>
  );
}
